/**
 * TakeExamPage — student-facing entry point for an Online Exam.
 *
 * Route: /school/:tenantId/exam/:examId/take
 *
 * Composes the existing OnlineExamLauncher (which owns the timed exam-taking
 * UI) with a thin subject picker + status guards so a student can drive the
 * full flow from the StudentPortal:
 *
 *   1. GET /tenants/{tid}/exams/{eid}            → exam metadata + status
 *   2. GET /tenants/{tid}/exam-subjects/{eid}    → list of paper subjects
 *   3. (per subject) launcher starts the session via /online/start
 *
 * The page is the entry point so we never have to expose the admin
 * SchoolExams.jsx tree to students.
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, BookOpen, Clock, AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { getToken } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const apiCall = async (path) => {
  const res = await fetch(`${API_URL}/api/school${path}`, {
    headers: { 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const d = body.detail;
    const msg = (d && typeof d === 'object') ? (d.message || d.reason || JSON.stringify(d)) : (d || 'Request failed');
    throw new Error(msg);
  }
  return body;
};

export default function TakeExamPage() {
  const { tenantId, examId } = useParams();
  const navigate = useNavigate();
  const [exam, setExam] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [subjectMap, setSubjectMap] = useState({});  // id → name
  const [selectedSubjectId, setSelectedSubjectId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [OnlineExamLauncher, setOnlineExamLauncher] = useState(null);

  // Lazy-load the launcher only when we actually need to render it.
  // Keeps the initial bundle for the picker small.
  useEffect(() => {
    let alive = true;
    import('@/components/school/SchoolOnlineExam').then((m) => {
      if (alive) setOnlineExamLauncher(() => m.OnlineExamLauncher);
    });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [examRes, subjectRes, allSubjects] = await Promise.all([
          apiCall(`/tenants/${tenantId}/exams/${examId}`),
          apiCall(`/tenants/${tenantId}/exam-subjects/${examId}`),
          apiCall(`/tenants/${tenantId}/subjects`).catch(() => []),
        ]);
        if (!alive) return;
        setExam(examRes);
        setSubjects(subjectRes || []);
        const map = {};
        for (const s of allSubjects) map[s.id] = s.name;
        setSubjectMap(map);
      } catch (e) {
        toast.error(e.message || 'Failed to load exam');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [tenantId, examId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
      </div>
    );
  }
  if (!exam) {
    return <Guard title="Exam not found" subtitle="Or you are not assigned to it." onBack={() => navigate(-1)} />;
  }
  // Status guard — match the same lenient rules as the backend so we never
  // block a published exam just because the legacy status flag wasn't flipped.
  // Hard-block only when archived/completed/results-published, or when the
  // time window has clearly closed or hasn't opened.
  const now = new Date();
  const parseTs = (v, endOfDayForDateOnly = false) => {
    if (!v) return null;
    const s = String(v);
    // Date-only "YYYY-MM-DD" — JS parses these as midnight UTC which would
    // close the exam 24h before the listing UI says it closes. Match the
    // backend (school_portal._assessment_window_state) by extending date-only
    // close values to end-of-day UTC.
    if (s.length === 10 && s[4] === '-' && s[7] === '-') {
      const d = new Date(s + (endOfDayForDateOnly ? 'T23:59:59Z' : 'T00:00:00Z'));
      return isNaN(d.getTime()) ? null : d;
    }
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
  };
  const opensAt = parseTs(exam.available_from) || parseTs(exam.start_date);
  const closesAt = parseTs(exam.available_until, true) || parseTs(exam.end_date, true);
  let block = null;
  if (['archived', 'cancelled'].includes(exam.status)) block = 'Exam archived';
  else if (exam.status === 'completed') block = 'Exam is closed';
  else if (exam.status === 'published') block = 'Exam results published';
  else if (opensAt && now < opensAt) block = 'Exam has not opened yet';
  else if (closesAt && now > closesAt) block = 'Exam window has closed';
  else if (!exam.is_published && !['active', 'scheduled'].includes(exam.status) && !opensAt && !closesAt) {
    block = 'Exam not yet activated by your teacher';
  }
  if (block) {
    return <Guard
      icon={<Clock className="h-8 w-8 text-amber-500" />}
      title={block}
      subtitle={
        opensAt && now < opensAt ? `Opens ${opensAt.toLocaleString()}` :
        closesAt && now > closesAt ? `Closed ${closesAt.toLocaleString()}` :
        `Status: ${exam.status}`
      }
      onBack={() => navigate(-1)}
    />;
  }
  if (!subjects.length) {
    return <Guard
      icon={<AlertTriangle className="h-8 w-8 text-amber-500" />}
      title="No subjects configured for this exam"
      subtitle="Ask your teacher to add subjects and questions before opening this."
      onBack={() => navigate(-1)}
    />;
  }

  // Launcher hijacks the screen once a subject is picked.
  if (selectedSubjectId && OnlineExamLauncher) {
    return (
      <OnlineExamLauncher
        tenantId={tenantId}
        examId={examId}
        subjectId={selectedSubjectId}
        subjectName={subjectMap[selectedSubjectId] || ''}
        examName={exam.name}
        onClose={() => setSelectedSubjectId(null)}
      />
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-6" data-testid="take-exam-page">
      <div className="max-w-2xl mx-auto space-y-4">
        <button onClick={() => navigate(-1)} className="text-xs text-indigo-600 hover:underline flex items-center gap-1">
          <ArrowLeft className="h-3 w-3" /> Back
        </button>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-indigo-600" /> {exam.name}
            </CardTitle>
            <div className="flex flex-wrap gap-2 mt-1 text-xs text-slate-500">
              {exam.exam_type && <Badge variant="outline" className="capitalize">{exam.exam_type}</Badge>}
              {exam.start_date && <span>· {exam.start_date}{exam.end_date && exam.end_date !== exam.start_date ? ` → ${exam.end_date}` : ''}</span>}
            </div>
            {exam.edited_during_attempts && (
              <div className="mt-3 p-2.5 rounded-md bg-amber-50 border border-amber-300 text-amber-900 text-xs flex items-start gap-2"
                   data-testid="exam-edited-banner">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold">This exam was updated by your teacher{exam.last_disruptive_edit_at ? ` at ${new Date(exam.last_disruptive_edit_at).toLocaleString()}` : ''}.</p>
                  <p className="opacity-90">Please re-read all questions carefully before submitting.</p>
                </div>
              </div>
            )}
          </CardHeader>
          <CardContent>
            <p className="text-sm font-medium mb-2">Pick the paper you want to take:</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {subjects.map((s) => {
                // Per-subject window gate. Falls back to the exam-level
                // window when the subject doesn't define its own — matches
                // the backend enforcement in start_online_exam.
                const subOpens = parseTs(s.available_from) || opensAt;
                const subCloses = parseTs(s.available_until, true) || closesAt;
                const isUpcoming = subOpens && now < subOpens;
                const isClosed = subCloses && now > subCloses;
                const subBlock = isUpcoming ? `Opens ${subOpens.toLocaleString()}`
                  : isClosed ? `Closed ${subCloses.toLocaleString()}` : null;
                const disabled = !!subBlock;
                return (
                  <Card
                    key={s.subject_id}
                    className={`transition-colors ${disabled ? 'opacity-60 cursor-not-allowed border-amber-200' : 'cursor-pointer hover:border-indigo-300'}`}
                    onClick={() => { if (!disabled) setSelectedSubjectId(s.subject_id); }}
                    data-testid={`exam-subject-tile-${s.subject_id}`}
                    title={subBlock || ''}
                  >
                    <CardContent className="p-3 flex items-center justify-between">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-sm">{s.subject_name || subjectMap[s.subject_id] || 'Subject'}</p>
                        <p className="text-xs text-slate-500">
                          {s.duration_minutes || 60} min · {s.max_marks || 100} marks
                          {s.question_count != null ? ` · ${s.question_count} q` : ''}
                        </p>
                        {subBlock && (
                          <p className="text-[11px] mt-1 text-amber-700 font-medium" data-testid={`exam-subject-window-${s.subject_id}`}>
                            {isUpcoming ? <><Clock className="h-3 w-3 inline mr-0.5" /> Opens {subOpens.toLocaleString()}</> :
                                          <><AlertTriangle className="h-3 w-3 inline mr-0.5" /> Closed {subCloses.toLocaleString()}</>}
                          </p>
                        )}
                      </div>
                      {disabled ? (
                        <Badge className="bg-amber-100 text-amber-700 shrink-0">{isUpcoming ? 'Not yet' : 'Closed'}</Badge>
                      ) : (
                        <Badge className="bg-emerald-100 text-emerald-700 shrink-0">Start</Badge>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

const Guard = ({ icon, title, subtitle, onBack }) => (
  <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
    <Card className="max-w-md w-full" data-testid="take-exam-guard">
      <CardContent className="py-8 text-center space-y-3">
        {icon || <AlertTriangle className="h-8 w-8 text-amber-500 mx-auto" />}
        <h2 className="text-lg font-semibold">{title}</h2>
        {subtitle && <p className="text-sm text-slate-500">{subtitle}</p>}
        <Button onClick={onBack} variant="outline" className="mt-2" data-testid="take-exam-guard-back">Back</Button>
      </CardContent>
    </Card>
  </div>
);
